/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useRef, useState } from 'react';
import MonsterSelector from './MonsterSelector';
import MonsterDisplay from './MonsterDisplay';
import PreferenceService from './services/PreferenceService';
import DownloadJsonButton from './DownloadJsonButton';
import DivToImage from './DivToImage';
import DataLoader from './DataLoader';
import Open5e from './model/Open5e';
import TetraCube from './model/TetraCube';
import CacheBlock from './CacheBlock';
import printerImg from './dndimages/printer-icon.png';
import saveImg from './dndimages/save-icon.png';
import deleteImg from './dndimages/delete-icon.png';
import pnNpcImg from './dndimages/wand-icon.png';
import logoTrimmedImg from './dndimages/LogoTrimmed.png';
import MonsterSearch from './MonsterSearch';
import CharacterModal from './character/CharacterModal';
import MessageModal from './character/MessageModal';
import Monster from './model/Monster';
import Spell from './model/Spell';
import NpcData from './model/NpcData';
import DataModel from './model/DataModel';
import Login from './auth/Login';
import { auth, firestoreService } from './services/FirestoreService';
import { onAuthStateChanged, User } from 'firebase/auth';
import ObjectHelper from './model/ObjectHelper';
import PrivacyNotice from './auth/PrivacyNotice';
import SubscriptionRenewalModal from './auth/SubscriptionRenewalModal';
import TourGuide from './tour/TourGuide';
import PropertySet from './PropertySet';
import DownloadVttFormatButton from './DownloadVttFormatButton';

const MonsterContainer: React.FC = () => {
  const exportImageDivSource = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState<boolean>(true);
  const [useMm2024, setUseMm2024] = useState<boolean>(false);
  const [showCache, setShowCache] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showAccount, setShowAccount] = useState<boolean>(false);
  const [mon, setMon] = useState<Monster>(new Monster());
  const [showWarnMessage, setShowWarnMessage] = useState<boolean>(true);
  const [isWarnMessageOpen, setWarnMessageOpen] = useState<boolean>(false);
  const [isPcModalOpen, setPcModalOpen] = useState<boolean>(false);
  const [searchMinCr, setSearchMinCr] = useState<number | null>(null);
  const [searchMaxCr, setSearchMaxCr] = useState<number | null>(null);
  const [searchKeywords, setSearchKeywords] = useState<string>('');
  const [searchPageSize, setSearchPageSize] = useState<number | null>(null);
  const [searchCurrentPage, setSearchCurrentPage] = useState<number | null>(null);
  const [searchSortBy, setSearchSortBy] = useState<{ id: string; desc: boolean }[]>([]);
  const [spellsBySlug, setSpellsBySlug] = useState<Map<string, Spell>>(new Map());
  const [dataModel] = useState<DataModel>(new DataModel());
  const [searchEnvironments, setSearchEnvironments] = useState<string[]>([]);
  const [, setForceUpdate] = useState(0); // Dummy state to force re-renders
  const [myMonsters, setMyMonsters] = useState<Monster[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [userData, setUserData] = useState<Record<string, any> | null>(null);
  const [monsterLimit, setMonsterLimit] = useState<number>(0);
  const [environments, setEnvironments] = useState<string[]>(['Water', 'Mountains', 'Desert', 'Hills']);
  const [showPrivacy, setShowPrivacy] = useState<boolean>(false);
  const [selectedMonsterId, setSelectedMonsterId] = useState<string>('');
  const [tourStep, setTourStep] = useState<number>(0);
  const [showTour, setShowTour] = useState<boolean>(false);
  const [openIndex, setOpenIndex] = useState<number>(-1);
  const [openBlock, setOpenBlock] = useState<string>('');
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [envOpen, setEnvOpen] = useState<boolean>(false);
  const [spellbookOpen, setSpellbookOpen] = useState<boolean>(false);
  const [tourState, setTourState] = useState<string>('');
  const [watchingUser, setWatchingUser] = useState<boolean>(false);
  const [showRenewalModal, setShowRenewalModal] = useState<boolean>(false);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState<boolean>(false);

  const updateEnvironments = () => {
    const allEnvs = dataModel.monsters.data.flatMap(mon => mon.environments || []);
    const uniqueEnvs = Open5e.cleanupEnvironments(Array.from(new Set(allEnvs)));
    setEnvironments(uniqueEnvs);
  }
  useEffect(() => {
    updateEnvironments();
  }, [dataModel, dataModel.monsters]);
  
  useEffect(() => {
    // Watch for auth state changes
    onAuthStateChanged(auth, (user: User | null) => {
      setUser(user);
      if (user) {
        watchUserMonsters();
        const watching = firestoreService.watchUserData(setUserData);
        setWatchingUser(watching);
      } else {
        setMyMonsters([]);
      }
    });

    // See if we need to show the tour for first-time users:
    const getTourState = async() => {
      const showTour = await PreferenceService.getUserPreference("showTour");
      if (showTour !== 'false') {
        setShowTour(true);
      }
    }
    getTourState();

    // Load the last monster from local storage when the component mounts
    const lastMonster = localStorage.getItem('currentMonster');
    const lastShowAll = localStorage.getItem('showAll');
    if (lastMonster) {
      const rawObj = JSON.parse(lastMonster);
      const monster = Monster.convertFromJson(rawObj);
      setMon(monster);
    }
    if (lastShowAll != null) {
      setShowAll(lastShowAll === "true");
    }

    dataModel.subscribe(() => {
      setForceUpdate((prev) => prev + 1); // force a redraw
      const newSpellsBySlug = new Map<string, Spell>();
      for (const clss of dataModel.spellsBySpellLevelByClass.keys()) {
        const classSpells = dataModel.spellsBySpellLevelByClass.get(clss);
        for (const level of classSpells?.keys() || []) {
          const levelSpells = classSpells?.get(level);
          for (const spell of levelSpells || []) {
            newSpellsBySlug.set(spell.slug, spell);
          }
        }
      }
      setSpellsBySlug(newSpellsBySlug);
    });

    // First check for the cached data:
    dataModel.fetchAll(true);
    // And then check for non-cached data, if anything is missing:
    dataModel.fetchAll(false, updateEnvironments);

    // Cleanup on unmount
    return () => {
      dataModel.subscribe(() => { }); // No-op to prevent stale subscriptions
    };

  }, []);

  const onUserChanged = (newUser: User | null) => {
    // Don't call setUserData(newUser) - newUser is a Firebase User object, not Firestore user data
    // The Firestore user data will be set correctly by watchUserData below
    if (!watchingUser) {
      setTimeout(() => setWatchingUser(firestoreService.watchUserData(setUserData)), 100);
    }
  }

  const onCloseTour = () => {
    setShowTour(false);
    PreferenceService.setUserPreference('showTour', 'false');
  }

  // Save the current monster to local storage whenever it changes
  useEffect(() => {
    if (mon) {
      const monsterString = JSON.stringify(mon);
      localStorage.setItem('currentMonster', monsterString);
    }
  }, [mon, mon?.npcData]);
  
  useEffect(() => {
    localStorage.setItem('showAll', showAll.toString());
  }, [showAll]);

  useEffect(() => {
    localStorage.setItem('showWarnMessage', showWarnMessage.toString());
  }, [showWarnMessage]);

  useEffect(() => {
    let subscriptionExpired = false;
    let monsterLimit = 0;
    if (userData) {
      // check if subscription is expired
      const expirationDate = userData['subscriptionExpirationDate'];
      if (expirationDate) {
        // Convert Firestore Timestamp to Date if needed
        const expirationDateObj = expirationDate.toDate ? expirationDate.toDate() : new Date(expirationDate);
        subscriptionExpired = expirationDateObj < new Date();
      }
      if (!subscriptionExpired) {
        monsterLimit = userData['monsterLimit'] || 0;
      }
      setIsSubscriptionExpired(subscriptionExpired);
    }
    setMonsterLimit(monsterLimit);
  }, [userData, userData?.subscriptionExpirationDate, userData?.monsterLimit]);

  const handleUpdateMonster = (field: string, value: any) => {
    setMon(prevMon => Monster.updateMonster(prevMon, field, value, false));
  };

  // helper function to convert a monster into a serializable object to write to the firestore DB
  const toPlainObject = (m: Monster): Record<string, any> => {
    const data: any = prepMonsterForSave(m);

    // Preserve empty arrays for ability fields before removeEmpty filters them out
    const abilityFields = ['spells', 'actions', 'bonus_actions', 'reactions', 'legendary_actions', 'lair_actions', 'traits', 'mythic_actions', 'regional_effects'];
    const emptyAbilityFields: Record<string, any[]> = {};
    abilityFields.forEach(field => {
      if (Array.isArray(data[field]) && data[field].length === 0) {
        emptyAbilityFields[field] = [];
      }
    });

    const cleanedData: any = ObjectHelper.removeEmpty(data);

    // Restore empty arrays for ability fields that were filtered out
    Object.entries(emptyAbilityFields).forEach(([field, value]) => {
      cleanedData[field] = value;
    });

    const sortedData = ObjectHelper.sortObjectKeys(cleanedData);
    return sortedData;
  }

  const handleMyMonsterAdd = () => {
    if (user && user.emailVerified) {
      if (isSubscriptionExpired) {
        setShowRenewalModal(true);
        return;
      }
      setSelectedMonsterId('user_' + mon.name);
      firestoreService.addOrUpdateMonsterToCollection(mon, toPlainObject);
    } else {
      setShowAccount(true);
    }
  }
  const handleMyMonsterRemove = () => {
    if (user && user.emailVerified) {
      setSelectedMonsterId('');
      firestoreService.deleteMonsterFromCollection(mon.name);
    } else {
      setShowAccount(true);
    }
  }
  
  const watchUserMonsters = () => {
    firestoreService.watchUserMonsters(spellsBySlug, (newMyMonsters: Monster[]) => setMyMonsters(newMyMonsters));
  };

  const handleMonsterChange = async (monsterId: string | null) => {
    setSelectedMonsterId(monsterId || '');
    if (monsterId) {
      console.log(`Selected monster ID: ${monsterId}`);

      // Check if trying to load a user monster with expired subscription
      if (monsterId.startsWith('user_') && isSubscriptionExpired) {
        setShowRenewalModal(true);
        return;
      }

      const doubleColumns = mon.doubleColumns;
      let newMon = null;
      // preserve the double column value
      if (monsterId.startsWith('user_')) {
        const monsterName = monsterId.substring('user_'.length);
        newMon = myMonsters.filter(m => m.name === monsterName)[0];
        if (newMon) {
          newMon = structuredClone(newMon);
        }
      } else {
        const open5eMon = dataModel.monsters.data.filter(m => m.slug == monsterId);
        if (open5eMon && open5eMon.length === 1) {
          newMon = Open5e.translateMonster(structuredClone(open5eMon[0]));
        }
      }
      if (newMon) {
        newMon.doubleColumns = doubleColumns;
        Monster.validate(newMon);
        setMon(newMon);
      }
      setShowAll(false);
    } else {
      setMon(new Monster());
      setShowAll(true);
    }
    setShowSearch(false);
  };

  const handleFileLoaded = (file: File | FileList): void => {
    if (file instanceof FileList) {
      return;
    }

    const reader = new FileReader();

    if (
      file.name.toLowerCase().endsWith('.monster') ||
      file.name.toLowerCase().endsWith('.dnd') ||
      file.name.toLowerCase().endsWith('.json')
    ) {
      reader.onload = (e) => {
        const fileContent = e?.target?.result as string;
        try {
          const jsonObject: any = JSON.parse(fileContent);
          if (file.name.toLowerCase().endsWith('.monster')) {
            const mon = TetraCube.convertLegacyMonsterToCurrent(jsonObject);
            setMon(mon);
          } else if (
            file.name.toLowerCase().endsWith('.dnd') ||
            file.name.toLowerCase().endsWith('.json')
          ) {
            const mon = Monster.convertFromJson(jsonObject);
            NpcData.updateSpellsFromSlugs(mon.npcData, spellsBySlug);
            if (mon.image.src.length > maxImageSize) {
              scaleDownImage(mon.image.src, (resizedDataUrl) => {
                mon.image.src = resizedDataUrl;
              });
            }
            setMon(mon);
          }
          setShowAll(false);
        } catch (error) {
          console.error('Error parsing JSON file:', error);
        }
      };
      reader.readAsText(file);
    } else if (file.type.startsWith('image/')) {
      reader.onload = (e) => {
        const originalDataUrl = e?.target?.result as string;
        if (file.size > 500_000) {
          scaleDownImage(originalDataUrl, (resizedDataUrl) => {
            handleUpdateMonster('image.src', resizedDataUrl);
            resetImageSettings();
          });
        } else {
          handleUpdateMonster('image.src', originalDataUrl);
          resetImageSettings();
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const openPcModal = () => {
    if (user && user.emailVerified) {
      if (isSubscriptionExpired) {
        setShowRenewalModal(true);
        return;
      }
      setPcModalOpen(true);
    } else {
      setShowAccount(true);
    }
  }

  const closePcModal = (npcMon: Monster | null) => {
    if (npcMon) {
      setMon(npcMon);
      const monsterString = JSON.stringify(mon);
      localStorage.setItem('currentMonster', monsterString);
    }
    setPcModalOpen(false);
  };

  const resetImageSettings = () => {
    handleUpdateMonster('image.rotation', 0);
    handleUpdateMonster('image.horizontalFlip', false);
    handleUpdateMonster('image.behind', false);
    handleUpdateMonster('image.clip', true);
  };

  const maxImageSize = 500_000;

  const scaleDownImage = (dataUrl: string, callback: (resizedDataUrl: string) => void) => {
    // Validate data URL format
    if (!dataUrl.startsWith('data:image/')) {
      console.error('Invalid image data URL');
      callback(''); // Return empty string on error
      return;
    }

    const img = new Image();
    img.onerror = () => {
      console.error('Error loading image data URL');
      callback(''); // Return empty string on error
    };

    if (dataUrl.length <= maxImageSize) {
      // Image was already smaller than max size, but still validate
      img.onload = () => callback(dataUrl);
    } else {
      // Set timeout to prevent long-running operations
      const timeout = setTimeout(() => {
        console.error('Image processing timed out');
        callback('');
      }, 10000); // 10 second timeout
      
      img.onload = () => {
        try {
          clearTimeout(timeout);
          const ratio = maxImageSize / dataUrl.length;
          let perAxis = Math.sqrt(ratio);
          let resizedDataUrl = dataUrl;
          let attemptsLeft = 20; // Prevent infinite loops
          const canvas = window.document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          while (ctx && resizedDataUrl.length > maxImageSize && attemptsLeft--) {
            
            console.log(`reducing image size by ${perAxis*100}%`);
        
            const width = Math.max(1, Math.round(img.width * perAxis));
            const height = Math.max(1, Math.round(img.height * perAxis));
            
            canvas.width = width;
            canvas.height = height;
            
            ctx.clearRect(0, 0, width, height);
            ctx.drawImage(img, 0, 0, width, height);
            
            // Use 'image/png' to preserve transparency
            resizedDataUrl = canvas.toDataURL('image/png');

            // In case we have to repeat this while loop, make the resulting image 10% smaller on each axis.
            // We always use img as our source, which does not change, so there are no compounding errors.
            perAxis *= 0.9;
          }
          canvas.remove();
          if (resizedDataUrl.length > maxImageSize) {
            console.error('Unable to resize image.');
            callback('');
          } else {
            callback(resizedDataUrl);
          }
        } catch (error) {
          console.error('Error during image resizing:', error);
          callback('');
        }
      };
    }
    img.src = dataUrl;
  };

  const handleShowAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    setShowAll(currentVal => !currentVal);
  }

  const handleUseMm2024 = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    setTourState(useMm2024 ? 'useLegacy' : 'useMm2024');
    setUseMm2024(currentVal => !currentVal);
  }

  const handleTwoColumnsClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    event.stopPropagation();
    handleUpdateMonster('doubleColumns', !mon.doubleColumns);
  }

  const handleOpenPrintView = () => {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      console.error('Failed to open new window');
      return;
    }

    try {
      const stylesHtml = Array.from(document.styleSheets)
                              .map(sheet => {
          try {
            if (!sheet.cssRules) return '';
  
            const cssText = Array.from(sheet.cssRules)
                                  .map(rule => (rule.cssText.startsWith('body') ? '' : rule.cssText))
                                  .filter(o => o)
                                  .join('\n');
  
            return cssText ? `<style>${cssText}</style>` : '';
          } catch (e) {
            console.warn('Unable to access stylesheet:', sheet.href);
            return '';
          }
        })
                              .join('');
  
      const content = window.document.getElementById('content-to-print')?.innerHTML || '';
      const className = 'print' + (useMm2024 ? ' mm2024' : '');
      const printHtml = `
        <html>
          <head>${stylesHtml}</head>
          <body>
            <div class='${className}'>${content}</div>
          </body>
        </html>
      `;
  
      newWindow.document.write(printHtml);
      newWindow.document.close();
      newWindow.focus();
    } catch (error) {
      console.error('Error preparing print view:', error);
    }
  };

  const buyMeKoffee = (
      <a href='https://ko-fi.com/Q5Q8WUEG1' target='_blank' rel="noreferrer">
        <img height='34' src='https://storage.ko-fi.com/cdn/kofi6.png?v=3' alt='Buy Me a Coffee at ko-fi.com' />
      </a>
  );

  const prepMonsterForSave = (m: Monster): Monster => {
    if (m.npcData) {
      // clear these out, so the file is smaller. We will re-get them
      // from the spells list on load from the corresponding slug lists.
      m.npcData.spellbookSpellsArrayBySpellLevelPrimary = [];
      m.npcData.spellbookSpellsArrayBySpellLevelSecondary = [];
      m.npcData.spellsPreparedArrayBySpellLevelPrimary = [];
      m.npcData.spellsPreparedArrayBySpellLevelSecondary = [];
    }
    setTourState('monster saved');
    return m;
  }
  
  const topLeft = (<div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    color: 'white',
    padding: '10px',
    fontFamily: 'imprint MT Shadow, Arial, sans-serif',
    fontSize: '24px',
    fontWeight: '500',
    zIndex: 500,
    textShadow: '-2px 2px 2px black', // Adds the drop shadow with 2 pixels of blurring
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '-16px' }}>
      <a href="http://greengodgames.com" style={{ textDecoration: 'none', color: 'inherit' }}>
        <img src={logoTrimmedImg} alt="Green God Games" height="64px" style={{ marginRight: '8px' }} />
      </a>
      <a href="http://greengodgames.com" style={{ textDecoration: 'none', color: 'inherit' }}>
        <div style={{ flex: '1', textAlign: 'left', marginLeft: '-15px' }}>Green God Games</div>
      </a>
    </div>
  </div>);

  const tourButtonStyle = {
    borderRadius: '50%',
    border: '3px solid white',
    fontSize: 'small',
    maxWidth: '20px',
    maxHeight: '20px',
    fontWeight: '700',
    paddingLeft: '4px',
    paddingTop: '0px',
    color: 'white',
    backgroundColor: '#2929fa'
  }
  const topRight = (<div style={{
    top: 0,
    textAlign: 'right',
    color: 'white',
    padding: '10px',
    fontSize: '12px',
    fontWeight: '400',
    zIndex: 500,
    textShadow: '-2px 2px 2px black', // Adds the drop shadow with 2 pixels of blurring
  }}>
    <button style={tourButtonStyle} onClick={() => {setTourStep(1); setShowTour(true)}} id="startTour"
            title={tourStep === 0 ? 'Start/Restart Tour' : 'Resume Tour'}>?</button> &nbsp; 
    <Login showAccount={showAccount} setShowAccount={setShowAccount} onUserChanged={onUserChanged}/>
  </div>);

  const monsterInMyMonsters = user != null && myMonsters && myMonsters.filter(o => o.name === mon.name).length > 0;
  return (
    <div className='main-content fadeInElement'>
      {topLeft}
      {!showPrivacy && topRight}
      {showPrivacy && <PrivacyNotice onClose={() => setShowPrivacy(false)} />}
      <SubscriptionRenewalModal
        isOpen={showRenewalModal}
        monsterCount={myMonsters.length}
        onRenew={() => {
          setShowRenewalModal(false);
          setShowAccount(true);
        }}
        onClose={() => setShowRenewalModal(false)}
        expirationDate={userData?.subscriptionExpirationDate?.toDate ? userData.subscriptionExpirationDate.toDate() : userData?.subscriptionExpirationDate}
      />
        {showTour && (
          <TourGuide  currentStepIndex={tourStep}
                      selectedMonsterId={selectedMonsterId}
                      mon={mon}
                      openBlock={openBlock}
                      openIndex={openIndex}
                      searchOpen={searchOpen}
                      spellbookOpen={spellbookOpen}
                      showAll={showAll}
                      tourState={tourState}
                      searchMinCr={searchMinCr} searchMaxCr={searchMaxCr} searchSortBy={searchSortBy}
                      searchKeywords={searchKeywords} searchSelectedEnvironments={searchEnvironments}
                      setCurrentStepIndex={(index: number) => setTourStep(index)}
                      onClose={onCloseTour}/>
          )}
      <div className='layerImage'></div>
      <br />
      <div className={ 'monster-container' + (useMm2024 ? ' mm2024' : '')}>
        <div style={{
          fontSize: '48px', textAlign: 'center', position: 'relative', color: '#224444',
          fontFamily: 'Gabriola', fontWeight: '800', textShadow: '0px 0px 3px white'
        }}><span id="mainTitle">Monster Forge</span></div>
        <div className="select-container" style={{ width: '100%' }}>
          <span>Select monster:</span>
          <MonsterSelector onMonsterChange={handleMonsterChange} selectedMonsterId={selectedMonsterId} dataModel={dataModel} myMonsters={myMonsters} />
          {
            <MonsterSearch isOpen={showSearch} setIsOpen={(isOpen) => {setShowSearch(isOpen); setTourState(isOpen ? 'monsterGridOpen' : 'monsterGridClosed')} } handleMonsterChange={handleMonsterChange}
              minCr={searchMinCr} maxCr={searchMaxCr} keywords={searchKeywords} selectedEnvironments={searchEnvironments} dataModel={dataModel}
              pageSize={searchPageSize} currentPage={searchCurrentPage} sortBy={searchSortBy}
              onUpdateMinCr={setSearchMinCr} onUpdateMaxCr={setSearchMaxCr} onUpdateKeywords={setSearchKeywords} onUpdateSelectedEnvironments={setSearchEnvironments}
              onUpdatePageSize={setSearchPageSize} onUpdateCurrentPage={setSearchCurrentPage} onUpdateSort={setSearchSortBy} />
          }
          <table title={(user && user.emailVerified) ? '' : 'These feature are only available when you are logged in.'} style={{border: '0px solid black'}} id="loggedInFeatures">
            <tbody>
            <tr>
              <td style={{fontSize: 'smaller'}}>My&nbsp;monsters:<br/>
              ({myMonsters?.length || 0 } / {monsterLimit})</td>
              <td id='myMonsterSection'>
                <button onClick={handleMyMonsterAdd} className='myMonsterButton'
                        disabled={(!monsterInMyMonsters && monsterLimit > 0 && myMonsters?.length >= monsterLimit)}
                        title={(!user || !user.emailVerified) ? 'These feature are only available when you are logged in.' :
                               isSubscriptionExpired ? 'Your subscription has expired. Click to renew and continue using this feature.' :
                               (!monsterInMyMonsters && myMonsters?.length >= monsterLimit) ? 'You have no room for additional monsters.\nPurchase or upgrade your subscription to increase your limit' :
                                          monsterInMyMonsters ? 'Update the current monster with this name in the \'My Monsters\' list' :
                                                                'Add this monster to the \'My Monsters\' section of the monster selector'} >
                  <img src={saveImg} alt="{name}" width='16px' style={{opacity: (!monsterInMyMonsters && myMonsters?.length >= monsterLimit) ? '0.3' : '1.0'}} />
                  {monsterInMyMonsters ? 'Update' : 'Add'}
                </button>
                <button onClick={handleMyMonsterRemove} className='myMonsterButton' disabled={!monsterInMyMonsters}
                      title={(!user || !user.emailVerified) ? 'These feature are only available when you are logged in.' :
                                       !monsterInMyMonsters ? 'Monster not in DB' :
                                                              'Removes the current monster with this name from the \'My Monsters\' list'}>
                  <img src={deleteImg} alt="{name}" width='16px' style={{opacity: !monsterInMyMonsters ? '0.3' : '1.0'}} /> Remove
                </button>
              </td>
              <td id='pcNpcWizardSection'>
                <button onClick={openPcModal} className='npcButton'
                        title={isSubscriptionExpired ? 'Your subscription has expired. Click to renew and continue using this feature.' : ''}>
                  <img src={pnNpcImg} alt="{name}" width='16px' /> Create<br/>PC/NPC
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div id="content-to-print" style={{textAlign: 'center'}}>
          <MonsterDisplay mon={mon}
            handleUpdateMonster={handleUpdateMonster}
            onFileDropped={handleFileLoaded}
            onClosePopups={() => setShowSearch(false)}
            exportImageDivSource={exportImageDivSource}
            dataModel={dataModel}
            showAll={showAll} useMm2024={useMm2024} twoColumns={mon.doubleColumns}
            openBlock={openBlock} setOpenBlock={setOpenBlock}
            openIndex={openIndex} setOpenIndex={setOpenIndex}
            searchOpen={searchOpen}
            setSearchOpen={setSearchOpen}
            spellbookOpen={spellbookOpen}
            setSpellbookOpen={setSpellbookOpen}
            onRefreshTour={setTourState}
             />
        </div>
        <div style={{textAlign: 'center'}}>
          <div style={{background: 'lightgrey', fontSize: 'small', maxWidth: mon.doubleColumns ? '800px' : '400px', minWidth: '400px', width: '100%', display: 'inline-block'}}>
            {mon.doubleColumns && (
              <div className="container-horizontal">
                <div className="container-horizontal-left"><b>Meta information:</b></div>
                <div className="container-horizontal-center">Source: <b>{mon.doc_ref.document__title}</b></div>
                <div className="container-horizontal-right">ID: <b>{mon.doc_ref.slug}</b></div>
              </div>)}
            {!mon.doubleColumns && (<>
                <div className="container-horizontal">
                  <div className="container-horizontal-left"><b>Meta information:</b></div>
                  <div className="container-horizontal-right">ID: <b>{mon.doc_ref.slug}</b></div>
                </div>
                <div className="container-horizontal">
                  <div className="container-horizontal-left">Source: <b>{mon.doc_ref.document__title}</b></div>
                  <div className="container-horizontal-right">License: <a href={mon.doc_ref.document__license_url} target="_blank">{mon.doc_ref.document__license_url}</a></div>
                  </div>
              </>)}
            <div className="container-horizontal">
              <div className="container-horizontal-left">
                <PropertySet className='environments' columns={4} isOpen={envOpen} setIsOpen={setEnvOpen} alwaysShow={true}
                              name="Environments" options={environments} values={mon.environments}
                              onSave={(vals: string) => handleUpdateMonster("environments", vals.split(/, ?/))}
                              onClickOutside={() => setEnvOpen(false)}/>
              </div>
              {mon.doubleColumns && <div className="container-horizontal-right">License: <a href={mon.doc_ref.document__license_url} target="_blank">{mon.doc_ref.document__license_url}</a></div>}
            </div>
          </div>
        </div>
        <div>
          <div style={{textAlign:'center'}}>
            <span id="layoutControls">
              <span>
                <input onChange={handleTwoColumnsClick} id="twoCols" type="checkbox" checked={mon?.doubleColumns ?? true} readOnly />
                <label htmlFor="twoCols">Two Columns</label>
              </span>
              &nbsp; &nbsp;
              <span style={{ textWrap: 'nowrap', display: mon.doubleColumns ? '' : 'none' }}>
                Move Items between columns:
                <button className="move-column-button" onClick={() => handleUpdateMonster('splitPoint', Math.max(0, (mon.splitPoint + 1)))} title='Move the top item from the right column to the bottom of the left column'>⮰</button>
                <button className="move-column-button" onClick={() => handleUpdateMonster('splitPoint', Math.max(0, (mon.splitPoint - 1)))} title='Move the bottom item from the left column to the top of the right column'>⮳</button>
              </span>
            </span>
            &nbsp; &nbsp;
            <span className='no-wrap' id="bottomEdgeControls">bottom edge:
              <button className="move-column-button" onClick={() => handleUpdateMonster('extraWhitespace', Math.max(0, (mon.extraWhitespace - 1)))} title='Reduce the whitespace at the bottom of the columns'>⏶</button>
              <button className="move-column-button" onClick={() => handleUpdateMonster('extraWhitespace', Math.max(0, (mon.extraWhitespace + 1)))} title='Increase the whitespace at the bottom of the columns'>⏷</button>
            </span>
          </div>
          <div className='container-horizontal'>
            <span className='container-horizontal-left'>
              <span id="showEmptyBlocks" >
                <input onChange={handleShowAllClick} id="showAll" type="checkbox" checked={showAll} readOnly />
                <label htmlFor="showAll">Show Empty Blocks</label>
              </span>
            </span>
            <span className='container-horizontal-right'>
              <span id="showUseMm2024">
                <input onChange={handleUseMm2024} id="useMm2024" type="checkbox" checked={useMm2024} readOnly />
                <label htmlFor="useMm2024">Use MM2024 style</label>
              </span>
            </span>
          </div>
        </div>

        <div id='SaveMonsterSection'>
          <DownloadJsonButton getData={() => prepMonsterForSave(mon)} filename={mon.name + '.json'} />
          <DownloadVttFormatButton getData={() => prepMonsterForSave(mon)} filename={mon.name + '.mod'} />
          <DivToImage targetRef={exportImageDivSource} filename={mon.name + '.png'} />
          <button onClick={handleOpenPrintView} title='open new tab with a black & white view of the monster, suitable for printing'>
            <img src={printerImg} alt="{name}" width='16px' /> Print View
          </button>
        </div>
        <span id='LoadMonsterSection'><DataLoader name='Load Monster' fileTypes='.monster, .dnd, .json' onFileLoad={handleFileLoaded} title='Import monster from a .monster/.dndo.json file'/></span>
        <DataLoader name='Load Image' fileTypes='image/*' onFileLoad={handleFileLoaded} title='Add (or replace) an image of this monster'/>
        <span className='no-wrap'>(or drag either file type onto page)</span>

        <div style={{ display: mon.image?.src ? 'block' : 'none' }} id='imageControls'>
          <span className='no-wrap'>
            <button onClick={() => handleUpdateMonster('image.src', null)} title='Remove the current image from the monster'>Clear Image</button>
            Size: <input type="range" min="1" max="800" value={mon?.image?.width || 400} style={{ width: '13%' }}
              onChange={(event) => handleUpdateMonster('image.width', parseInt(event.target.value))} />
            Opacity: <input type="range" min="0" max="100" value={(mon?.image?.opacity || 1) * 100} style={{ width: '8%' }}
              onChange={(event) => handleUpdateMonster('image.opacity', parseFloat(event.target.value) / 100.0)} />
          </span>
          <span className='no-wrap'>
            Rotate:<button className="rotate_button" onClick={() => handleUpdateMonster('image.rotation', mon.image?.rotation - 1)} title='Rotate the image 90 degrees counter-clockwise'>↶</button>
            <button className="rotate_button" onClick={() => handleUpdateMonster('image.rotation', mon.image?.rotation + 1)} title='Rotate the image 90 degrees clockwise'>↷</button>
            <input id='hflip' type="checkbox" checked={mon?.image?.horizontalFlip || false}
              onChange={(event) => handleUpdateMonster('image.horizontalFlip', event.target.checked)} />
            <label htmlFor='hflip'>Horizontal Flip</label>
            <input id='imgBehind' type="checkbox" checked={mon?.image?.behind || false}
              onChange={(event) => handleUpdateMonster('image.behind', event.target.checked)} />
            <label htmlFor='imgBehind'>Behind</label>
            <input id='imgClip' type="checkbox" checked={mon?.image?.clip || false}
              onChange={(event) => handleUpdateMonster('image.clip', event.target.checked)} />
            <label htmlFor='imgClip'>Clip</label>
          </span>
        </div>
        <div>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px'}}>
            <table id="monster-alias-table">
              <tbody>
                <tr><th colSpan={2}>Short / Plural Names:</th></tr>
                <tr>
                  <td style={{textAlign: 'right'}}>Short ([MON]):</td>
                  <td>
                    <input type="text" value={mon?.shortName || ''} onChange={(event) => handleUpdateMonster('shortName', event.target.value)} id="short-name-selector" />
                  </td>
                </tr>
                <tr>
                  <td style={{textAlign: 'right'}}>Plural ([MONS]):</td>
                  <td>
                    <input type="text" value={mon?.pluralName || ''} onChange={(event) => handleUpdateMonster('pluralName', event.target.value)} id="plural-name-selector" />
                  </td>
                </tr>
              </tbody>
            </table>
            <div style={{textAlign: 'center', background: '#CCCCCC80', border: '1px solid black', padding: '2px 10px'}}>
              Have feedback, comments, bugs or suggestions?<br />
              I'd love to hear it! <a href="mailto:postrowski@gmail.com?subject=GGG Stats Block" target="_blank" rel="noreferrer">Send me an email</a><br />
            <div>Or, if you love it, send me a tip: {buyMeKoffee}</div>
            </div>
          </div>
          <div>
            <MessageModal isOpen={isWarnMessageOpen}
              onClose={(showInFuture: boolean, accept: boolean) => {
                setWarnMessageOpen(false);
                if (accept) {
                  setShowWarnMessage(showInFuture);
                  setPcModalOpen(true);
                }
              }} />
            <CharacterModal isOpen={isPcModalOpen} onClose={closePcModal} mon={mon} dataModel={dataModel} />
            {showAll && !showCache && <button className='cacheButton' onClick={() => setShowCache(true)}>Show Cache</button>}
            {showAll && showCache && <button className='cacheButton' onClick={() => setShowCache(false)}>Hide Cache</button>}
            {showAll && showCache && <CacheBlock show={true} showClearButton={true} dataModel={dataModel} />}
          </div>
          <div style={{ paddingBottom: '130px' }}>&nbsp;</div>
        </div>
      </div>
      <div style={{ color: 'white', textAlign: 'right', textShadow: '1px 1px 2px black' }} title="This application includes material from the System Reference Document 5.1 (SRD 5.1), published by Wizards of the Coast under the Creative Commons Attribution 4.0 International License. Content is provided via Open5e.com and is not published, owned, or endorsed by Wizards of the Coast or Green God Games. SRD 5.1 is licensed under CC BY 4.0, and the full text of the license can be found at https://creativecommons.org/licenses/by/4.0/.”">Content provided by <a style={{ color: '#BBBBFF' }} target="_blank" href="https://open5e.com/" rel="noreferrer">open5e.com</a>, under the <a style={{ color: '#BBBBFF' }} target="_blank" href="https://creativecommons.org/licenses/by/4.0/." rel="noreferrer">Creative Commons Attribution 4.0 License</a></div>
      <div style={{ color: 'white', textAlign: 'right', textShadow: '1px 1px 2px black' }}>Copyright © 2025 Green God Games, LLC. All Rights Reserved.</div>
    </div>
  );
};

export default MonsterContainer;
